import { Component, OnDestroy, OnInit } from '@angular/core'
import { AlertController, MenuController, ModalController, NavController } from '@ionic/angular/standalone'
import { AuthService } from 'src/app/core/auth.service'
import { Profile } from 'src/app/models/profile.model'
import { config, environment } from 'src/environments/environment'
import { NavigationEnd, Router } from '@angular/router'
import { ConfirmationService } from 'primeng/api'
import { ActiveForm, ActiveFormContext, ActiveFormService } from 'src/app/services/active-form.service'
import { FormRecoveryService } from 'src/app/services/form-recovery.service'
import { AppInsightsService } from 'src/app/services/app-insights.service'
import { Observable, Subscription, filter, firstValueFrom, timeout } from 'rxjs'
import { FirednaProfileService } from 'src/app/services/firedna-profile.service'
import { Collection, SubCollection } from 'src/app/models/firedna-collections.model'
import { DatastoreCollectionsService } from 'src/app/services/datastore-collections.service'
import { OfflineTransactionsService } from 'src/app/services/offline-transactions.service'
import { NetworkStatusService } from 'src/app/services/network-status.service'
import { ConnectionStatus } from '@capacitor/network'
import { TransactionlogPage } from 'src/app/modals/transactionlog/transactionlog.page'
import { Capacitor } from '@capacitor/core'
import { Badge } from '@capawesome/capacitor-badge'
import { DatastoreService } from 'src/app/services/datastore.service'
import { ProfileOverviewPage } from 'src/app/modals/profile-overview/profile-overview.page'
import { DatastoreMessagesService } from 'src/app/services/datastore-messages.service'
import { MessageGroupsModel, visibleReceivedMessages } from 'src/app/models/message.model'
import { SystemSetupService } from 'src/app/services/system-setup.service'
import { ScanTagService } from 'src/app/services/scan-tag.service'
import { LiveUpdateService } from 'src/app/services/live-update.service'
import { Role } from 'src/app/models/role'
import { FEATURES } from 'src/app/shared/feature-flags'
import { DialogService } from 'primeng/dynamicdialog'
import { SETTINGS_ROUTES, hasAnySettings } from 'src/app/shared/settings-tiles'
import { PENDING_CAPTURE_AUTO_RESUME_MAX_AGE_MS, PendingCaptureService } from 'src/app/services/pending-capture.service'

@Component({
  selector: 'app-app',
  templateUrl: './app.page.html',
  styleUrls: ['./app.page.scss'],
  standalone: false,
})
export class AppPage implements OnInit, OnDestroy {
  public profile$: Observable<Profile>
  public profile: Profile
  public messages$: Observable<MessageGroupsModel>
  mediaUrl: any
  resizeFunctionUrl: any
  // The inset menu remembers its collapsed state per device; the classic one always starts minimised
  splitPaneClosed: boolean = FEATURES.insetNav ? AppPage.readNavCollapsed() : true
  // True while the menu is docked beside the content (≥992px); below that it is a slide-out drawer
  splitPaneVisible: boolean = false
  /** Settings stays highlighted on every page its tiles open */
  settingsActive = false
  currentApplicationVersion = environment.appVersion
  readonly features = FEATURES
  public networkStatus$: Observable<ConnectionStatus>
  public networkStatus: ConnectionStatus = {
    connected: true,
    connectionType: 'wifi',
  }
  firstLoad: boolean = true
  showMFAbar: boolean = true

  totalOfflineTransactions: number = 0
  totalOfflineImages: number = 0

  Role = Role

  currentYearLong(): number {
    return new Date().getFullYear()
  }

  public isMobile = Capacitor.isNativePlatform()
  public datastoreOfflineCollections$: Observable<Collection[]>

  private sub: Subscription = new Subscription()

  syncInProgress$: Observable<boolean>
  datastoreSyncInProgress$: Observable<boolean>

  updateAvailable$: Observable<boolean>

  constructor(
    private offlineTransactionsService: OfflineTransactionsService,
    private scanTagService: ScanTagService,
    private datastore: DatastoreService,
    private menu: MenuController,
    private alertController: AlertController,
    private auth: AuthService,
    private networkStatusService: NetworkStatusService,
    private router: Router,
    private modalController: ModalController,
    private profileService: FirednaProfileService,
    private datastoreMessagesService: DatastoreMessagesService,
    private navCtrl: NavController,
    private systemSetupService: SystemSetupService,
    private liveUpdateService: LiveUpdateService,
    private activeFormService: ActiveFormService,
    private formRecoveryService: FormRecoveryService,
    private confirmationService: ConfirmationService,
    private appInsights: AppInsightsService,
    private datastoreCollections: DatastoreCollectionsService,
    private pendingCapture: PendingCaptureService,
    private dialogService: DialogService
  ) {}

  ngOnInit() {
    if (!environment.production) {
      this.isMobile = true
    }
    this.updateSettingsActive(this.router.url)
    this.sub.add(
      this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe((e) => this.updateSettingsActive(e.urlAfterRedirects))
    )
    //
    this.systemSetupService.init()
    this.updateAvailable$ = this.liveUpdateService.updateAvailable$
    void this.offerResumeActiveForm()

    this.mediaUrl = config.mediaUrl
    this.resizeFunctionUrl = config.resizeFunctionUrl

    this.profile$ = this.profileService.profileData$
    this.sub.add(
      this.profile$.subscribe({
        next: (value) => {
          // console.log('profile', value, this.showMFAbar)

          this.profile = value
          if (this.profile.mfaEnabled === true) {
            this.showMFAbar = false
          }
        },
      })
    )
    this.messages$ = this.datastoreMessagesService.messages$
    this.syncInProgress$ = this.offlineTransactionsService.syncInProgress$
    this.datastoreSyncInProgress$ = this.datastore.syncInProgress$
    this.offlineTransactionsService.processOfflineTransactions().then(() => {
      this.sub.add(
        this.offlineTransactionsService.getOfflineTransactions('all').subscribe((data) => {
          // console.log('offlineTransactionsService' , data);
          this.totalOfflineTransactions = data.length - data.filter((x) => x.url.includes('files')).length
          this.totalOfflineImages = data.filter((x) => x.url.includes('files')).length
          Badge.isSupported().then(async (result) => {
            if (result.isSupported) {
              const count = this.totalOfflineTransactions + this.totalOfflineImages
              if (count > 0) {
                await Badge.set({ count })
              } else {
                await Badge.clear()
              }
            }
          })
        })
      )
    })

    // for use in template
    this.networkStatus$ = this.networkStatusService.status$

    // console.log('AppPage:ngOnInit');
    this.sub.add(
      this.networkStatusService.status$.subscribe(async (data) => {
        // console.log('networkStatusService:change', data)
        this.networkStatus = data
        if (this.networkStatus.connected !== undefined) {
          if (this.networkStatus.connected === true && this.firstLoad === false) {
            // load all data stores
            console.log('AppPage:ngOnInit:networkStatusService')
            const alert = await this.alertController.create({
              header: 'Network detected',
              message: 'You have just connected to a network. Would you like to sync your cache?',
              buttons: [
                {
                  text: 'Ok',
                  handler: () => {
                    this.datastore.loadDataStore()
                  },
                },
                {
                  text: 'Cancel',
                  role: 'cancel',
                },
              ],
            })

            await alert.present()
          } else if (this.networkStatus.connected === true && this.firstLoad === true) {
            this.datastore.loadDataStore()
            this.firstLoad = false
          } else {
            this.offlineRouteRedirect()
          }
        }
      })
    )
  }

  /**
   * After a crash / renderer restart the page a user was on is gone but its draft is not.
   * If a form was open when the app last ran and its draft still exists, offer to reopen it.
   */
  private async offerResumeActiveForm() {
    const rendererGoneAt = await this.activeFormService.consumeRendererGone()
    // A camera result Android re-delivered after tearing the app down mid-capture (copied at boot)
    await this.pendingCapture.ensureRestoreSettled()
    const pending = await this.pendingCapture.load()
    console.info('[AppPage] offerResumeActiveForm', { rendererGoneAt, pending })
    const active = await this.activeFormService.load()
    if (rendererGoneAt) {
      const previous = await this.activeFormService.getPreviousSession()
      // Was the app in the background when it was killed (camera / another app in front)?
      const backgrounded = !!previous.backgroundedAt && previous.backgroundedAt.getTime() <= rendererGoneAt.getTime()
      this.appInsights.logEvent('WebViewRendererGone', {
        at: rendererGoneAt.toISOString(),
        platform: Capacitor.getPlatform(),
        pendingCapture: !!pending,
        pendingCaptureDelivered: !!pending?.file,
        // Paths only: the form's label holds customer location text
        activeForm: active?.url.split('?')[0] ?? '',
        lastRoute: previous.lastRoute ?? '',
        backgrounded,
        backgroundedForMs: backgrounded ? rendererGoneAt.getTime() - previous.backgroundedAt!.getTime() : -1,
      })
    }
    if (!active) {
      return
    }
    const draft = await this.formRecoveryService.loadFormData(active.draftId)
    if (!draft) {
      await this.activeFormService.clear()
      return
    }
    // Already on the form (a browser refresh keeps the URL): the page offers the draft itself,
    // so a second prompt here would just double up
    if (this.router.url === active.url) {
      return
    }
    const deliveredCapture = pending?.file && pending.draftId === active.draftId ? pending : null
    // They were literally mid-tap in the camera moments ago: a modal between the camera and the
    // form reads as a bug, so go straight back. Offline can't rebuild the form (collection cache
    // is memory-only), so that case keeps the prompt and its "No connection" branch.
    if (
      deliveredCapture &&
      this.pendingCapture.ageMs(deliveredCapture) < PENDING_CAPTURE_AUTO_RESUME_MAX_AGE_MS &&
      (await this.networkStatusService.getNetworkStatusConnected())
    ) {
      this.appInsights.logEvent('PendingCaptureAutoResume', { captureKey: deliveredCapture.captureKey, ageMs: this.pendingCapture.ageMs(deliveredCapture) })
      await this.openActiveForm(active)
      return
    }
    const timeAgo = this.formRecoveryService.getTimeAgo(new Date(draft.timestamp))
    const crashNote = rendererGoneAt ? ' The app restarted after running out of memory, but your answers and photos were kept.' : ''
    const photoNote = deliveredCapture ? ' The photo you had just taken was kept too.' : ''
    this.confirmationService.confirm({
      header: 'Resume unsaved work?',
      message: `You have an unsaved <strong>${active.label}</strong> from ${timeAgo}.${crashNote}${photoNote} Resume it now?`,
      icon: 'pi pi-history',
      acceptLabel: 'Resume',
      rejectLabel: 'Not now',
      acceptButtonStyleClass: 'p-button-outlined',
      rejectButtonStyleClass: 'p-button-text',
      accept: async () => {
        if (!this.networkStatus.connected) {
          // The collection cache is memory-only, so the page can't build its form without a
          // connection; keep everything and let the in-page prompt offer the draft later
          const alert = await this.alertController.create({
            header: 'No connection',
            message: 'Your draft is safe. Reconnect and open the report to carry on where you left off.',
            buttons: ['OK'],
          })
          await alert.present()
          return
        }
        await this.openActiveForm(active)
      },
      reject: async () => {
        // The draft is still offered when the report itself is reopened
        await this.activeFormService.clear()
      },
    })
  }

  /**
   * Reopens the form the way the user originally reached it — building → floor → product → form —
   * so the header back button works and back / post-save land on the building, not the dashboard.
   * Intermediate pages are pushed without animation; only the final form transition animates.
   * If the building can't be resolved (unknown ids, datastore slow), falls back to opening the
   * form as the root exactly as before.
   */
  private async openActiveForm(active: ActiveForm) {
    // The page applies the draft without asking again — the user just said yes in the prompt
    const separator = active.url.includes('?') ? '&' : '?'
    const target = `${active.url}${separator}resumeDraft=1`

    const ctx = await this.resolveFormContext(active.context)
    if (!ctx.collectionId) {
      await this.navCtrl.navigateRoot(target)
      return
    }

    // Same root the building list uses (collection-manager.page navigateRoot → collection-info)
    await this.navCtrl.navigateRoot(`/app/collection-manager/collection-info?collection=${ctx.collectionId}`, { animated: false })
    if (ctx.subCollectionId) {
      await this.navCtrl.navigateForward(['/app/collections/subcollection'], { queryParams: { subCollectionId: ctx.subCollectionId }, animated: false })
    }
    if (ctx.productId) {
      await this.navCtrl.navigateForward(['/app/products/firedoor/view'], { queryParams: { id: ctx.productId }, animated: false })
    } else if (ctx.customProductId) {
      await this.navCtrl.navigateForward(['/app/products/custom-product/view-custom-product'], { queryParams: { id: ctx.customProductId }, animated: false })
    }
    await this.navCtrl.navigateForward(target)
  }

  /**
   * Fills in the building / floor for a product-level form from the collection cache. Reads
   * `collections$` directly (no structuredClone — the datastore getters clone the whole set) and
   * gives up after a bounded wait so a slow first load can't leave the user stuck after tapping Resume.
   */
  private async resolveFormContext(ctx: ActiveFormContext = {}): Promise<ActiveFormContext> {
    const productId = ctx.productId ?? ctx.customProductId
    const needsLookup = !ctx.collectionId || (!ctx.subCollectionId && !!productId)
    if (!needsLookup) {
      return ctx
    }
    const collections = await firstValueFrom(
      this.datastoreCollections.collections$.pipe(
        filter((x) => x.length > 0),
        timeout(20000)
      )
    ).catch(() => [] as Collection[])

    for (const collection of collections) {
      if (ctx.collectionId && collection.id !== ctx.collectionId) {
        continue
      }
      const sub = this.findSubCollection(collection.subCollections ?? [], ctx.subCollectionId, productId)
      if (sub) {
        return { ...ctx, collectionId: collection.id, subCollectionId: ctx.subCollectionId ?? sub.id }
      }
      if (ctx.collectionId === collection.id) {
        return ctx
      }
    }
    return ctx
  }

  private findSubCollection(subs: SubCollection[], subCollectionId?: string, productId?: string): SubCollection | undefined {
    for (const sub of subs) {
      if (subCollectionId && sub.id === subCollectionId) {
        return sub
      }
      if (productId && sub.combinedProducts?.some((p) => p.id === productId)) {
        return sub
      }
      const nested = this.findSubCollection(sub.subCollections ?? [], subCollectionId, productId)
      if (nested) {
        return nested
      }
    }
    return undefined
  }

  private static readonly NAV_COLLAPSED_KEY = 'fdna.navCollapsed'

  private static readNavCollapsed(): boolean {
    try {
      return localStorage.getItem(AppPage.NAV_COLLAPSED_KEY) === 'true'
    } catch {
      return false
    }
  }

  get showSettings(): boolean {
    return hasAnySettings({ hasRole: (role) => this.hasRole(role), profileType: this.profile?.profileType, isMobile: this.isMobile })
  }

  private updateSettingsActive(url: string) {
    const path = url.split(/[?#]/)[0]
    this.settingsActive = SETTINGS_ROUTES.some((route) => path === route || path.startsWith(route + '/'))
  }

  /** Icon-only menu: tooltips stand in for the hidden labels */
  get navCollapsed(): boolean {
    return this.splitPaneVisible && this.splitPaneClosed
  }

  onSplitPaneVisible(event: CustomEvent<{ visible: boolean }>) {
    this.splitPaneVisible = event.detail.visible
  }

  profileInitials(name: string | undefined): string {
    const parts = (name ?? '').trim().split(/\s+/).filter(Boolean)
    return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase()
  }

  toggleMenu() {
    this.splitPaneClosed = !this.splitPaneClosed
    try {
      localStorage.setItem(AppPage.NAV_COLLAPSED_KEY, String(this.splitPaneClosed))
    } catch {
      // Storage unavailable (private mode) — the choice just isn't remembered
    }
    setTimeout(() => {
      // needs to wait for css transition so needs to be a timeout
      window.dispatchEvent(new Event('resize'))
    }, 200)
  }

  enableMFA() {
    this.auth.enableMFA().then(async (data) => {
      console.log('enableMFA', data)
      let alert: any
      if (data === true) {
        alert = await this.alertController.create({
          header: 'Success',
          backdropDismiss: false,
          message: 'MFA has been enabled on your account.  You will now be logged out.',
          buttons: [
            {
              text: 'OK',
              handler: () => {
                console.log('logout')

                this.auth.logoutUser()
              },
            },
          ],
        })
      } else if (data === false) {
        alert = await this.alertController.create({
          header: 'Error',
          message: 'MFA has not been enabled on your account.',
          buttons: [
            {
              text: 'OK',
              handler: () => {},
            },
          ],
        })
      } else {
        alert = await this.alertController.create({
          header: 'Error',
          message: data,
          buttons: ['OK'],
        })
      }
      await alert.present()
    })
  }

  hideMFA() {
    this.auth.hideMFA().then(async (data) => {
      console.log('hideMFA', data)

      this.showMFAbar = !data
      console.log('showMFAbar', this.showMFAbar)
    })
  }

  toggleAppOffline() {
    this.networkStatusService.toggleConnected()
  }

  offlineRouteRedirect() {
    if (!this.networkStatus.connected) {
      const route = this.router.url.substring(0, 11)
      const offlineRoutes = ['/app/dashbo', '/app/collec', '/app/audits', '/app/produc', '/app/custom', '/app/job-sc']
      console.log('changed network', offlineRoutes.includes(route))
      if (!offlineRoutes.includes(route)) {
        this.navCtrl.navigateRoot(['/app/dashboard'])
      }
    }
  }
  async updateApp() {
    console.log('updateApp in app.page.ts')
    await this.liveUpdateService.showAlert()
  }

  ngOnDestroy(): void {
    console.log('app:ngOnDestroy')
    this.sub.unsubscribe()
  }

  presentProfileOverview() {
    this.closeMenu()
    this.dialogService.open(ProfileOverviewPage, {
      showHeader: false,
      modal: true,
      dismissableMask: true,
      closeOnEscape: true,
      width: '560px',
      breakpoints: { '640px': 'calc(100vw - 24px)' },
      contentStyle: { padding: '0', overflow: 'hidden' },
      styleClass: 'full-bleed-dialog profile-menu-dialog',
    })
  }

  async notConnectedAlert() {
    const alert = await this.alertController.create({
      header: 'No connection',
      message: 'Please ensure your device has a data connection',
      buttons: [
        {
          text: 'Okay',
          handler: () => {},
        },
      ],
    })
    await alert.present()
  }

  async offlineDataAlert() {
    if (this.networkStatus.connected) {
      if (this.totalOfflineTransactions > 0) {
        const alert = await this.alertController.create({
          header: 'Sync offline data',
          message: 'Please ensure you have a stable connection',
          buttons: [
            {
              text: 'Sync',
              handler: () => {
                this.offlineTransactionsService.processOfflineTransactions(true).catch(() => {})
              },
            },
            {
              text: 'View',
              handler: async () => {
                const modal = await this.modalController.create({
                  component: TransactionlogPage,
                  componentProps: { view: 'data' },
                })
                return await modal.present()
              },
            },
          ],
        })
        await alert.present()
      } else {
        const alert = await this.alertController.create({
          header: 'Sync offline data',
          message: 'Your data is up to date',
          buttons: [
            {
              text: 'Okay',
              handler: () => {},
            },
          ],
        })
        await alert.present()
      }
    } else {
      this.notConnectedAlert()
    }
  }

  async processImages() {
    if (this.networkStatus.connected) {
      if (this.totalOfflineImages > 0) {
        const alert = await this.alertController.create({
          header: 'Sync offline images',
          message: 'Please ensure you have a stable connection before proceeding',
          buttons: [
            {
              text: 'Process queue',
              handler: () => {
                this.offlineTransactionsService.processOfflineImages(true).catch(() => {})
              },
            },
            {
              text: 'View queue',
              handler: async () => {
                const modal = await this.modalController.create({
                  component: TransactionlogPage,
                  componentProps: { view: 'images' },
                })
                return await modal.present()
              },
            },
            {
              text: 'Cancel',
              role: 'cancel',
              cssClass: 'secondary',
              handler: () => {
                console.log('Confirm Cancel: blah')
              },
            },
          ],
        })
        await alert.present()
      } else {
        const alert = await this.alertController.create({
          header: 'Sync offline images',
          message: 'Your images are up to date',
          buttons: [
            {
              text: 'Okay',
              handler: () => {},
            },
          ],
        })
        await alert.present()
      }
    } else {
      this.notConnectedAlert()
    }
  }

  async resetPassword() {
    const alert = await this.alertController.create({
      header: 'Password reset',
      message: 'You will receive an email with instructions.',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => {
            console.log('Confirm Cancel: blah')
          },
        },
        {
          text: 'Okay',
          handler: () => {
            console.log('Confirm Okay')
            this.auth.resetPassword(this.profile.email)
          },
        },
      ],
    })

    await alert.present()
  }
  openMenu() {
    // console.log("open menu");
    this.menu.open('main')
  }
  closeMenu() {
    // console.log("close menu");
    this.menu.close('main')
  }

  /** Unread count for the avatar badge — only messages this user can see in the inbox. */
  unreadMessageCount(messages: MessageGroupsModel): number {
    return visibleReceivedMessages(messages.messagesTo, this.hasRole('messageService')).filter((m) => !m.msgRead).length
  }

  hasRole(role: string) {
    if (this.auth.hasRole(role)) {
      return true
    } else {
      return false
    }
  }
  hasRoleFromArray(roles: string[]) {
    // console.log(this.auth.hasRoleFromArray(roles));
    if (this.auth.hasRoleFromArray(roles)) {
      // console.log('true');
      return true
    } else {
      // console.log('false');
      return false
    }
  }

  scanActionSheet() {
    this.scanTagService.scanActionSheet('private')
  }
}
